/**
 * Email Templates Service
 *
 * Manages email templates stored in Firestore.
 * Path: teams/{teamId}/emailTemplates/{templateId}
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type {
  EmailTemplate,
  EmailTemplateContext,
  EmailTemplateVars,
} from '@/types/emailTemplates';
import { logger } from '@/lib/logger';

// =============================================================================
// Collection Reference
// =============================================================================

const emailTemplatesCollection = (teamId: string) =>
  collection(db, 'teams', teamId, 'emailTemplates');

const emailTemplateDoc = (teamId: string, templateId: string) =>
  doc(db, 'teams', teamId, 'emailTemplates', templateId);

// =============================================================================
// Default Templates (used for seeding)
// =============================================================================

export const DEFAULT_EMAIL_TEMPLATES: Omit<
  EmailTemplate,
  'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
>[] = [
  // Customer context
  {
    id: 'customer-general',
    name: 'Algemene vraag',
    description: 'Stel een vraag aan de klant',
    channel: 'email',
    context: 'customer',
    subjectTemplate: 'Vraag over uw auto',
    bodyTemplate: `Beste {{customer.displayName}},

Ik heb een vraag over uw auto.`,
    isDefault: true,
    isSystem: true,
    order: 1,
  },
  {
    id: 'customer-followup',
    name: 'Offerte opvolging',
    description: 'Vraag of de klant de offerte heeft ontvangen',
    channel: 'email',
    context: 'customer',
    subjectTemplate: 'Opvolging offerte',
    bodyTemplate: `Beste {{customer.displayName}},

Graag wil ik even navragen of u onze offerte heeft ontvangen en of u nog vragen heeft.

Ik hoor graag van u.`,
    isSystem: true,
    order: 2,
  },
  {
    id: 'customer-thankyou',
    name: 'Bedankt voor uw bezoek',
    description: 'Bedank de klant na een bezoek',
    channel: 'email',
    context: 'customer',
    subjectTemplate: 'Bedankt voor uw bezoek',
    bodyTemplate: `Beste {{customer.displayName}},

Bedankt voor uw bezoek aan onze garage. Wij hopen dat u tevreden bent met onze service.

Mocht u nog vragen hebben, dan horen wij dat graag.`,
    isSystem: true,
    order: 3,
  },
  // Work Order context
  {
    id: 'workorder-status',
    name: 'Status update',
    description: 'Informeer de klant over de voortgang',
    channel: 'email',
    context: 'workOrder',
    subjectTemplate: 'Status werkorder {{workOrder.id}} - {{vehicle.licensePlate}}',
    bodyTemplate: `Beste {{customer.displayName}},

Hierbij een update over de werkzaamheden aan uw voertuig{{vehicle.licensePlate}}.

Werkorder: {{workOrder.id}}

[Vul hier de status update in]`,
    isDefault: true,
    isSystem: true,
    order: 1,
  },
  {
    id: 'workorder-ready',
    name: 'Voertuig gereed',
    description: 'Laat weten dat het voertuig opgehaald kan worden',
    channel: 'email',
    context: 'workOrder',
    subjectTemplate: 'Uw voertuig ({{vehicle.licensePlate}}) is gereed voor ophalen',
    bodyTemplate: `Beste {{customer.displayName}},

Goed nieuws! Uw voertuig ({{vehicle.licensePlate}}) is gereed voor ophalen.

U kunt uw voertuig ophalen tijdens onze openingstijden.`,
    isSystem: true,
    order: 2,
  },
  {
    id: 'workorder-quote',
    name: 'Offerte werkzaamheden',
    description: 'Stuur een offerte voor werkzaamheden',
    channel: 'email',
    context: 'workOrder',
    subjectTemplate: 'Offerte werkzaamheden - {{vehicle.licensePlate}}',
    bodyTemplate: `Beste {{customer.displayName}},

Naar aanleiding van de inspectie van uw voertuig ({{vehicle.licensePlate}}) sturen wij u hierbij een offerte voor de benodigde werkzaamheden.

[Vul hier de offerte details in]

Graag vernemen wij of u akkoord gaat met deze offerte.`,
    isSystem: true,
    order: 3,
  },
  // Invoice context
  {
    id: 'invoice-attached',
    name: 'Factuur bijgevoegd',
    description: 'Standaard factuur e-mail',
    channel: 'email',
    context: 'invoice',
    subjectTemplate: 'Factuur {{invoice.number}}',
    bodyTemplate: `Beste {{customer.displayName}},

Hierbij ontvangt u factuur {{invoice.number}}.

Mocht u vragen hebben over deze factuur, dan horen wij dat graag.`,
    isDefault: true,
    isSystem: true,
    order: 1,
  },
  {
    id: 'invoice-with-pdf-link',
    name: 'Factuur met PDF link',
    description: 'Factuur e-mail met download link naar PDF',
    channel: 'email',
    context: 'invoice',
    subjectTemplate: 'Factuur {{invoice.number}}',
    bodyTemplate: `Beste {{customer.displayName}},

Hierbij ontvangt u factuur {{invoice.number}}.

U kunt de factuur hier downloaden:
{{pdf.downloadUrl}}

Mocht u vragen hebben over deze factuur, dan horen wij dat graag.`,
    isSystem: true,
    order: 2,
  },
  {
    id: 'invoice-reminder',
    name: 'Betalingsherinnering',
    description: 'Herinnering voor openstaande factuur',
    channel: 'email',
    context: 'invoice',
    subjectTemplate: 'Betalingsherinnering factuur {{invoice.number}}',
    bodyTemplate: `Beste {{customer.displayName}},

Volgens onze administratie is factuur {{invoice.number}} nog niet voldaan.

Wij verzoeken u vriendelijk het openstaande bedrag zo spoedig mogelijk over te maken.

Mocht u reeds betaald hebben, dan kunt u deze herinnering als niet verzonden beschouwen.`,
    isSystem: true,
    order: 3,
  },
  // Appointment context
  {
    id: 'appointment-confirmation',
    name: 'Afspraak bevestiging',
    description: 'Bevestig een gemaakte afspraak',
    channel: 'email',
    context: 'appointment',
    subjectTemplate: 'Bevestiging afspraak - {{appointment.date}}',
    bodyTemplate: `Beste {{customer.displayName}},

Hierbij bevestigen wij uw afspraak op {{appointment.date}} voor voertuig {{vehicle.licensePlate}}.

Wij zien u graag op de afgesproken datum en tijd.`,
    isDefault: true,
    isSystem: true,
    order: 1,
  },
  {
    id: 'appointment-reminder',
    name: 'Afspraak herinnering',
    description: 'Herinner de klant aan een afspraak',
    channel: 'email',
    context: 'appointment',
    subjectTemplate: 'Herinnering: afspraak op {{appointment.date}}',
    bodyTemplate: `Beste {{customer.displayName}},

Dit is een herinnering voor uw afspraak op {{appointment.date}} voor voertuig {{vehicle.licensePlate}}.

Wij zien u graag!`,
    isSystem: true,
    order: 2,
  },
];

// =============================================================================
// CRUD Operations
// =============================================================================

/**
 * Get all email templates for a team
 */
export async function getEmailTemplates(teamId: string): Promise<EmailTemplate[]> {
  try {
    // Simple query without orderBy to avoid index requirement
    const snapshot = await getDocs(emailTemplatesCollection(teamId));

    if (snapshot.empty) {
      // No templates yet - seed with defaults
      logger.info('[emailTemplates] No templates found, seeding defaults for team:', teamId);
      await seedDefaultTemplates(teamId);
      // Fetch again after seeding
      const seededSnapshot = await getDocs(emailTemplatesCollection(teamId));
      const templates = seededSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as EmailTemplate[];
      // Sort client-side by order
      return templates.sort((a, b) => (a.order || 99) - (b.order || 99));
    }

    const templates = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as EmailTemplate[];

    // Sort client-side by order
    return templates.sort((a, b) => (a.order || 99) - (b.order || 99));
  } catch (error) {
    logger.error('[emailTemplates] Failed to get templates:', error);
    // Return default templates as fallback (not persisted, but usable)
    return DEFAULT_EMAIL_TEMPLATES.map((t) => ({
      ...t,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'system',
      updatedBy: 'system',
    })) as EmailTemplate[];
  }
}

/**
 * Get templates for a specific context
 */
export async function getEmailTemplatesForContext(
  teamId: string,
  context: EmailTemplateContext
): Promise<EmailTemplate[]> {
  const templates = await getEmailTemplates(teamId);
  return templates.filter((t) => t.context === context);
}

/**
 * Get a single template by ID
 */
export async function getEmailTemplateById(
  teamId: string,
  templateId: string
): Promise<EmailTemplate | null> {
  try {
    const docRef = emailTemplateDoc(teamId, templateId);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      return null;
    }

    return {
      id: snapshot.id,
      ...snapshot.data(),
    } as EmailTemplate;
  } catch (error) {
    logger.error('[emailTemplates] Failed to get template:', error);
    return null;
  }
}

/**
 * Create or update an email template
 */
export async function saveEmailTemplate(
  teamId: string,
  template: Partial<EmailTemplate> & { id: string },
  userId: string
): Promise<void> {
  const docRef = emailTemplateDoc(teamId, template.id);
  const existing = await getDoc(docRef);

  const now = serverTimestamp();

  if (existing.exists()) {
    // Update existing
    await setDoc(
      docRef,
      {
        ...template,
        updatedAt: now,
        updatedBy: userId,
      },
      { merge: true }
    );
  } else {
    // Create new
    await setDoc(docRef, {
      ...template,
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      updatedBy: userId,
    });
  }
}

/**
 * Delete an email template
 * Cannot delete system templates
 */
export async function deleteEmailTemplate(teamId: string, templateId: string): Promise<void> {
  const template = await getEmailTemplateById(teamId, templateId);

  if (template?.isSystem) {
    throw new Error('Cannot delete system template');
  }

  await deleteDoc(emailTemplateDoc(teamId, templateId));
}

/**
 * Seed default templates for a team
 */
export async function seedDefaultTemplates(teamId: string, userId?: string): Promise<void> {
  const now = new Date().toISOString();

  for (const template of DEFAULT_EMAIL_TEMPLATES) {
    const docRef = emailTemplateDoc(teamId, template.id);
    await setDoc(docRef, {
      ...template,
      createdAt: now,
      updatedAt: now,
      createdBy: userId || 'system',
      updatedBy: userId || 'system',
    });
  }

  logger.info('[emailTemplates] Seeded default templates for team:', teamId);
}

// =============================================================================
// Template Interpolation
// =============================================================================

/**
 * Interpolate placeholders in a template string
 * Replaces {{key}} with the corresponding value from vars
 */
export function interpolateTemplate(template: string, vars: EmailTemplateVars): string {
  return template.replace(/\{\{([^}]+)\}\}/g, (_, key) => {
    const trimmedKey = key.trim() as keyof EmailTemplateVars;
    const value = vars[trimmedKey];
    // Return empty string if value is undefined/null, otherwise return the value
    return value ?? '';
  });
}

/**
 * Get the default template for a context
 */
export async function getDefaultTemplateForContext(
  teamId: string,
  context: EmailTemplateContext
): Promise<EmailTemplate | null> {
  const templates = await getEmailTemplatesForContext(teamId, context);

  // First try to find one marked as default
  const defaultTemplate = templates.find((t) => t.isDefault);
  if (defaultTemplate) return defaultTemplate;

  // Otherwise return the first one
  return templates[0] || null;
}

/**
 * Get smart default template based on context and status
 */
export async function getSmartDefaultTemplate(
  teamId: string,
  context: EmailTemplateContext,
  options?: {
    invoiceStatus?: string;
    workOrderStatus?: string;
    appointmentDate?: string;
  }
): Promise<EmailTemplate | null> {
  const templates = await getEmailTemplatesForContext(teamId, context);

  if (templates.length === 0) return null;

  // Smart defaults based on status
  switch (context) {
    case 'invoice':
      if (options?.invoiceStatus === 'overdue') {
        const reminder = templates.find((t) => t.id === 'invoice-reminder');
        if (reminder) return reminder;
      }
      break;

    case 'workOrder':
      if (options?.workOrderStatus === 'DONE') {
        const ready = templates.find((t) => t.id === 'workorder-ready');
        if (ready) return ready;
      }
      break;

    case 'appointment':
      if (options?.appointmentDate) {
        const appointmentTime = new Date(options.appointmentDate).getTime();
        const now = Date.now();
        const hoursUntil = (appointmentTime - now) / (1000 * 60 * 60);
        if (hoursUntil > 0 && hoursUntil <= 24) {
          const reminder = templates.find((t) => t.id === 'appointment-reminder');
          if (reminder) return reminder;
        }
      }
      break;
  }

  // Fall back to default template
  return getDefaultTemplateForContext(teamId, context);
}
